import type { ReactNode } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useEffect, useRef, useState } from "react"
import {
	IconClockCoinsFill18,
	IconCreditCardPlusFill18,
	IconGaugeFill18,
} from "nucleo-ui-fill-18"
import { useDebounce } from "react-use"

import { TRIAL_AI_RESOLUTIONS } from "@productlane/lib/src/plans-client"
import { Button, Input, Switch } from "@productlane/ui"

import { toast } from "@/components/toast"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import { UsageGuard } from "./usage-guard"
import { usageFillClass } from "./usage-meter"
import { UsageRow } from "./usage-row"

// A typed cap commits this long after the last keystroke, so a half-typed "5"
// on the way to "500" never becomes the ceiling.
const CAP_COMMIT_DELAY_MS = 700

export interface CapDraft {
	value: number | null
	error: string | null
}

// What a typed cap means, before anything is written. An empty field is the
// user clearing the box to retype, never "remove the cap": that is its own
// button, and treating a blank field as unlimited is how a cap silently
// disappeared while the UI kept showing it.
export function readCapDraft(raw: string): CapDraft {
	const trimmed = raw.trim()
	if (trimmed === "") return { value: null, error: null }
	if (!/^\d+$/.test(trimmed)) {
		return { value: null, error: "Enter a whole number of resolutions." }
	}
	const parsed = Number(trimmed)
	if (!Number.isFinite(parsed)) {
		return { value: null, error: "Enter a whole number of resolutions." }
	}
	return { value: parsed, error: null }
}

export function AiUsageSection({
	form,
	pricing,
	onPersist,
	onAddPaymentMethod,
	hasPaymentMethod = false,
	trialDaysLeft = 0,
	notice,
}: {
	notice?: ReactNode
	form: UseFormReturn<{
		payPerUseEnabled: boolean
		monthlyLimit: number | null
	}>
	onPersist: (values: {
		payPerUseEnabled: boolean
		monthlyLimit: number | null
	}) => void
	onAddPaymentMethod: () => void
	hasPaymentMethod?: boolean
	trialDaysLeft?: number
	pricing:
		| {
				freeResolutions: number
				costPerResolutionCents: number
				usedThisMonth: number
				monthlyLimit: number | null
				renewalDate: Date | string | null
				trialing?: boolean
		  }
		| undefined
}) {
	const [capOpen, setCapOpen] = useState(false)

	const trialing = pricing?.trialing ?? false
	const payPerUseEnabled = form.watch("payPerUseEnabled") && !trialing
	const monthlyLimit = form.watch("monthlyLimit")
	const freeResolutions = pricing?.freeResolutions ?? 0
	const costPerResolutionCents = pricing?.costPerResolutionCents ?? 0
	const used = pricing?.usedThisMonth ?? 0
	const paidResolutions = Math.max(0, used - freeResolutions)
	const perResolutionFormatted = `$${(costPerResolutionCents / 100).toFixed(2)}`

	const paidLimit =
		monthlyLimit !== null && monthlyLimit >= 0 ? monthlyLimit : null
	// Resolutions past the cap were never billed, so the spend the guard names
	// is the capped one. Raw usage above it read as "$144.57 of $39.50".
	const billedResolutions =
		paidLimit === null ? paidResolutions : Math.min(paidResolutions, paidLimit)
	const costFormatted = `$${((billedResolutions * costPerResolutionCents) / 100).toFixed(2)}`
	const maxCostFormatted = `$${(((paidLimit ?? 0) * costPerResolutionCents) / 100).toFixed(2)}`

	const freeUsed = Math.min(used, freeResolutions)
	const freePercentage =
		freeResolutions > 0 ? Math.min((freeUsed / freeResolutions) * 100, 100) : 0
	const freeBarColor = usageFillClass({
		percent: freePercentage,
		stops: !payPerUseEnabled,
	})

	const paidPercentage =
		paidLimit !== null && paidLimit > 0
			? Math.min((paidResolutions / paidLimit) * 100, 100)
			: 0
	const paidBarColor = usageFillClass({
		percent: paidPercentage,
		stops: paidLimit !== null,
	})
	// An uncapped bar has no total to fill, so it shows a nub for "spending".
	const paidBarPercentage =
		paidLimit !== null ? paidPercentage : paidResolutions > 0 ? 15 : 0

	// A fixed locale, because the sentence around it is English: the browser's
	// own locale rendered "Oct 1" as "1. Okt." inside it.
	const renewalDateFormatted = pricing?.renewalDate
		? new Date(pricing.renewalDate).toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
			})
		: null

	const setPayPerUse = (value: boolean) => {
		form.setValue("payPerUseEnabled", value)
		form.setValue("monthlyLimit", null)
		onPersist({ payPerUseEnabled: value, monthlyLimit: null })
		setCapOpen(false)
	}

	const setCap = (limit: number | null) => {
		form.setValue("monthlyLimit", limit)
		form.clearErrors("monthlyLimit")
		onPersist({ payPerUseEnabled: true, monthlyLimit: limit })
	}

	// A trial spends one flat allowance, so pay-per-use has nothing to bill
	// against yet. The switch stays disabled and the click reports what unblocks
	// it: a card, or the trial converting for whoever already added one.
	const explainTrialBlock = () => {
		if (hasPaymentMethod) {
			const when =
				trialDaysLeft > 0
					? ` It converts in ${trialDaysLeft} ${trialDaysLeft === 1 ? "day" : "days"}.`
					: ""
			toast.info({
				id: "ai-pay-per-use-trial-blocked",
				title: "Pay-per-use starts when your trial converts",
				description: `Your trial includes ${TRIAL_AI_RESOLUTIONS} AI resolutions.${when}`,
				icon: IconClockCoinsFill18,
			})
			return
		}
		toast.info({
			id: "ai-pay-per-use-trial-blocked",
			title: "Add a card to turn on pay-per-use",
			description: `Your trial includes ${TRIAL_AI_RESOLUTIONS} AI resolutions.`,
			icon: IconCreditCardPlusFill18,
			action: {
				label: "Add payment details",
				callback: onAddPaymentMethod,
			},
		})
	}

	const guard = resolutionGuard({
		trialing,
		hasPaymentMethod,
		trialDaysLeft,
		payPerUseEnabled,
		freePercentage,
		paidPercentage,
		capped: paidLimit !== null,
		resetDate: renewalDateFormatted,
		costFormatted,
		maxCostFormatted,
	})

	const onGuardAction = () => {
		if (guard?.kind === "trial") onAddPaymentMethod()
		if (guard?.kind === "free-tier") setPayPerUse(true)
		if (guard?.kind === "cap") setCapOpen(true)
	}

	return (
		<SettingsContainer notice={notice}>
			<SettingItem
				title="AI resolutions"
				description="What the agent resolved this month, and where it stops."
			/>

			{guard && (
				<UsageGuard
					title={guard.title}
					description={guard.description}
					actionLabel={guard.action}
					icon={guard.icon}
					onAction={guard.action ? onGuardAction : undefined}
				/>
			)}

			<UsageRow
				className="pt-4"
				title={trialing ? "Included in trial" : "Included in your plan"}
				description={
					renewalDateFormatted
						? `Resets on ${renewalDateFormatted}`
						: "One allowance for the whole trial"
				}
				value={`${freeUsed} / ${freeResolutions}`}
				percent={freePercentage}
				fillClassName={freeBarColor}
			/>

			{payPerUseEnabled && (
				<UsageRow
					className="pt-4"
					title="Paid this month"
					description={`${paidResolutions} ${paidResolutions === 1 ? "resolution" : "resolutions"} at ${perResolutionFormatted}`}
					value={costFormatted}
					percent={paidBarPercentage}
					fillClassName={paidBarColor}
				/>
			)}

			{payPerUseEnabled && (
				<div className="flex flex-col gap-3 pt-4">
					<SettingItem
						title="Monthly cap"
						description={
							paidLimit === null
								? "No cap set, so paid resolutions run without a ceiling."
								: `${paidLimit} paid resolutions, up to ${maxCostFormatted} a month.`
						}
						action={
							<Button
								variant="outline"
								onClick={() => setCapOpen(!capOpen)}
								data-testid="cap-toggle"
							>
								{paidLimit === null ? "Set a cap" : "Adjust cap"}
							</Button>
						}
					/>
					{capOpen && (
						<CapEditor
							limit={paidLimit}
							perResolutionCents={costPerResolutionCents}
							onCommit={setCap}
						/>
					)}
				</div>
			)}

			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
			<div
				onClick={trialing ? explainTrialBlock : undefined}
				data-testid="pay-per-use-field"
				hidden={guard?.kind === "free-tier"}
				className="pt-4"
			>
				<Switch
					checked={payPerUseEnabled}
					disabled={trialing}
					switchClassName={trialing ? "pointer-events-none" : undefined}
					onCheckedChange={setPayPerUse}
					name="payPerUseEnabled"
					label="Enable pay-per-use"
					description={
						trialing
							? hasPaymentMethod
								? "Paid resolutions start when your trial converts."
								: "Paid resolutions need a payment method on file."
							: "The agent keeps answering past your plan."
					}
				/>
			</div>
		</SettingsContainer>
	)
}

// The cap's own field. It holds the typed text rather than a number, so an
// empty box stays empty instead of committing NaN, and it writes on a pause in
// typing or on blur, never on every keystroke.
function CapEditor({
	limit,
	perResolutionCents,
	onCommit,
}: {
	limit: number | null
	perResolutionCents: number
	onCommit: (limit: number | null) => void
}) {
	const [draft, setDraft] = useState(limit === null ? "" : String(limit))
	const committedRef = useRef(limit)

	// A cap written from anywhere else (the guard, another tab) replaces what is
	// in the box, so the field can never show a number the workspace does not have.
	useEffect(() => {
		if (limit === committedRef.current) return
		committedRef.current = limit
		setDraft(limit === null ? "" : String(limit))
	}, [limit])

	const commit = (raw: string) => {
		const { value, error } = readCapDraft(raw)
		if (error) {
			toast.info({
				title: "Caps are whole numbers of resolutions",
				description: `"${raw.trim()}" is not one. Enter a number like 50, or remove the cap.`,
				icon: IconGaugeFill18,
			})
			setDraft(
				committedRef.current === null ? "" : String(committedRef.current),
			)
			return
		}
		// An empty box is mid-edit, not a removal.
		if (value === null) return
		if (value === committedRef.current) return
		committedRef.current = value
		onCommit(value)
	}

	useDebounce(
		() => {
			if (draft.trim() === "") return
			const { value } = readCapDraft(draft)
			if (value === null || value === committedRef.current) return
			committedRef.current = value
			onCommit(value)
		},
		CAP_COMMIT_DELAY_MS,
		[draft],
	)

	const previewCents = readCapDraft(draft).value
	const preview =
		previewCents === null
			? "paid resolutions a month"
			: `paid resolutions a month, up to $${((previewCents * perResolutionCents) / 100).toFixed(2)}`

	return (
		<div className="flex items-center gap-2.5">
			<Input
				name="monthlyLimit"
				placeholder="e.g. 50"
				inputMode="numeric"
				value={draft}
				onChange={(event) => setDraft(event.target.value)}
				onBlur={(event) => commit(event.target.value)}
				className="w-28"
			/>
			<span className="text-xs text-tertiary">{preview}</span>
			{limit !== null && (
				<Button
					variant="ghost"
					size="sm"
					className="ml-auto"
					onClick={() => {
						committedRef.current = null
						setDraft("")
						onCommit(null)
					}}
				>
					Remove cap
				</Button>
			)}
		</div>
	)
}

interface Guard {
	kind: "trial" | "free-tier" | "cap"
	title: string
	description: string
	action?: string
	icon?: typeof IconCreditCardPlusFill18
}

// AI has stopped, and each reason has exactly one way out. The reset date rides
// along, since "when does this fix itself" is the other question they have.
export function resolutionGuard({
	trialing,
	hasPaymentMethod,
	trialDaysLeft,
	payPerUseEnabled,
	freePercentage,
	paidPercentage,
	capped,
	resetDate,
	costFormatted,
	maxCostFormatted,
}: {
	trialing: boolean
	hasPaymentMethod: boolean
	trialDaysLeft: number
	payPerUseEnabled: boolean
	freePercentage: number
	paidPercentage: number
	capped: boolean
	resetDate: string | null
	costFormatted: string
	maxCostFormatted: string
}): Guard | null {
	const resetSentence = resetDate
		? `Resolutions reset on ${resetDate}.`
		: "Resolutions reset on the 1st."

	if (trialing) {
		if (freePercentage < 100) return null
		if (hasPaymentMethod) {
			return {
				kind: "trial",
				title: "Your trial's AI resolutions are used up",
				description:
					trialDaysLeft > 0
						? `The agent starts again when your trial converts in ${trialDaysLeft} ${trialDaysLeft === 1 ? "day" : "days"}.`
						: "The agent starts again when your trial converts.",
				icon: IconClockCoinsFill18,
			}
		}
		return {
			kind: "trial",
			title: "Your trial's AI resolutions are used up",
			description:
				"A payment method starts the agent again and refreshes resolutions every month.",
			action: "Add payment details",
			icon: IconCreditCardPlusFill18,
		}
	}

	if (!payPerUseEnabled) {
		if (freePercentage < 100) return null
		return {
			kind: "free-tier",
			title: "The agent stopped resolving",
			description: `${resetSentence} Enable pay-per-use to start it again now.`,
			action: "Enable pay-per-use",
		}
	}

	if (!capped || paidPercentage < 100) return null
	return {
		kind: "cap",
		title: "The agent stopped at your monthly cap",
		description: `${costFormatted} of ${maxCostFormatted} used. ${resetSentence}`,
		action: "Adjust cap",
	}
}
